import { Clear, Done } from '@mui/icons-material'
import {
  Alert as MuiAlert,
  Box,
  Button,
  Grid,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material'
import dayjs from 'dayjs'
import React, { useContext, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  AccountsPayable,
  PayableStatus,
  PaymentMethod,
  useGetAccountPayableByVoucherLazyQuery,
  useMarkPayableAsPaidMutation,
  useSoftDeletePayableMutation,
  useVoidAndDuplicatePayableMutation,
  useVoidAndReissueCheckMutation
} from '../../../../generated'
import { GRAPHQL_QUERY_POLICY, NOT_APPLICABLE, SOMETHING_WENT_WRONG } from '../../../constants'
import { CardBox, CardBoxTopBar } from '../../../theme/styleComponents'
import { enumToLabel, formatCurrency, getProperPaymentMethod } from '../../../utils'
import { VoidPayableDialog } from './VoidPayableDialog'
import { Alert } from '../../common/Alert'
import { CaseDetailRow } from '../../common/CaseDetailRow'
import { ContentHead } from '../../common/ContentHead'
import { anchorOrigin, transformOrigin } from '../../../theme/styleConstants'
import { AppContext } from '../../../context'
import { ActionType } from '../../../context/AppContextReducer'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import AddAccountPayableModal from './AddAccountPayableModal'
import { ConfirmationModal } from '../../common/ConfirmationModal'
import { ApprovedCheckAssignment, printChecks } from '../../../../services'
import { AccountOption } from '../../../interfaceTypes/common'
import { ProcessPayableFormValues } from '../../../interfaceTypes/forms'
import { CheckModal } from './CheckModal'

type ConfirmationType = 'delete' | 'void' | 'markPaid' | 'voidAndDuplicate' | 'voidAndReissue'

// Void & Reissue Check is hidden: the firm voids the check (Void credits the client for a billed
// expense) and records a new payable for the replacement. The flow, its modal and the API mutation are
// kept for now; links between existing reissued pairs still show.
// TODO(cleanup): remove the Void & Reissue menu item, modal, mutation hook and the API
// voidAndReissueCheck flow once the firm confirms it is no longer needed.
const SHOW_VOID_AND_REISSUE = false

const PayableDetail = () => {
  const { voucherNo } = useParams()
  const { isOpen, dispatch } = useContext(AppContext)

  const [currentPayable, setCurrentPayable] = useState<AccountsPayable | null>(null)
  const [confirmationModal, setConfirmationModal] = useState<{
    show: boolean
    type: ConfirmationType
  }>({
    show: false,
    type: 'delete'
  })
  // Void failures are shown inside the open modal rather than as a toast — the guard messages
  // explain the alternative action and are too long to sit across the top of the page.
  const [actionError, setActionError] = useState<string | null>(null)
  // Printing a single voucher goes through the same modal the listing page uses, so the number this
  // page commits to is the one the server's plan produced, not a guess made here.
  const [openCheckModal, setOpenCheckModal] = useState(false)
  const [previewingCheck, setPreviewingCheck] = useState(false)
  const navigate = useNavigate()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleEditAccount = () => {
    handleMenuClose()
    return dispatch({ type: ActionType.SET_IS_OPEN, isOpen: !isOpen })
  }

  /**
   * Preview draws the check onto plain paper: nothing is written, no leaf is consumed and the payable
   * stays unpaid, so it skips the bank-account/starting-number modal entirely — same as Preview
   * Checks on the listing page.
   */
  const handlePreviewCheck = async () => {
    handleMenuClose()
    if (!currentPayable?.voucherNo) return

    setPreviewingCheck(true)
    try {
      await printChecks([currentPayable.voucherNo], true)
    } catch (error) {
      if (error instanceof Error) Alert.error('Error: ' + error.message)
    } finally {
      setPreviewingCheck(false)
    }
  }

  const handlePrintCheck = () => {
    handleMenuClose()
    setOpenCheckModal(true)
  }

  /**
   * Called by CheckModal with the voucher-to-leaf pairing the user approved. The API rebuilds the same
   * plan and refuses the run if it no longer matches, so this cannot print a number that was not on
   * screen.
   */
  const startPrintingCheck = async (
    _data: ProcessPayableFormValues,
    startingCheckNo?: string,
    bankAccountId?: string,
    assignments?: ApprovedCheckAssignment[]
  ) => {
    if (!currentPayable?.voucherNo) return

    try {
      await printChecks([currentPayable.voucherNo], false, startingCheckNo, bankAccountId, assignments)
      // Printing marks the voucher Paid and stamps its check number, so the page has to re-read it.
      refetchAccountPayable()
    } catch (error) {
      if (error instanceof Error) Alert.error('Error: ' + error.message)
    }
  }

  const actionButton = () => (
    <Button variant="contained" color="primary" endIcon={<ArrowDropDownIcon />} onClick={handleMenuClick}>
      Actions
    </Button>
  )

  const [getAccountPayable, { refetch: refetchAccountPayable }] = useGetAccountPayableByVoucherLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: {
      voucherNo: voucherNo || ''
    },
    onError(error) {
      Alert.error(error.message)
    },
    onCompleted(data) {
      const { getAccountPayableByVoucher } = data || {}
      const { accountPayableEntry, response } = getAccountPayableByVoucher || {}
      const { status } = response || {}
      if (status === 200) setCurrentPayable(accountPayableEntry as AccountsPayable)
    }
  })

  const [markPayableAsPaid, { loading: markingAsPaid }] = useMarkPayableAsPaidMutation({
    onError() {
      Alert.error(SOMETHING_WENT_WRONG)
    },
    onCompleted(data) {
      const { markPayableAsPaid } = data || {}
      const { response } = markPayableAsPaid || {}
      const { status } = response || {}
      if (status === 200) {
        Alert.success('Marked as paid successfully')
        refetchAccountPayable()
        handleMenuClose()
        setConfirmationModal(prev => ({ ...prev, show: false }))
      }
    }
  })

  const [softDeletePayable, { loading: softDeleting }] = useSoftDeletePayableMutation({
    onError() {
      Alert.error(SOMETHING_WENT_WRONG)
    },
    onCompleted(data) {
      const { softDeletePayable } = data || {}
      const { response } = softDeletePayable || {}
      const { status } = response || {}
      if (status === 200) {
        Alert.success('Deleted successfully')
        navigate('/accounts/payable', { replace: true })
      }
    }
  })

  const [voidAndDuplicatePayable, { loading: voidingAndDuplicating }] = useVoidAndDuplicatePayableMutation({
    onError(error) {
      setActionError(error.message || SOMETHING_WENT_WRONG)
    },
    onCompleted(data) {
      const { voidAndDuplicatePayable } = data || {}
      const { response, duplicatePayable: duplicate } = voidAndDuplicatePayable || {}
      const { status } = response || {}
      if (status === 200 && duplicate?.voucherNo) {
        Alert.success(`Voided and duplicated as ${duplicate.voucherNo}`)
        // Close before navigating: the target is this same route with a different param, so the component
        // is not remounted and the confirmation would stay open on top of the new voucher.
        setConfirmationModal(prev => ({ ...prev, show: false }))
        navigate(`/accounts/payable/${duplicate.voucherNo}`, { replace: true })
      }
    }
  })

  const [voidAndReissueCheck, { loading: voidingAndReissuing }] = useVoidAndReissueCheckMutation({
    onError(error) {
      // The API returns specific, actionable guard messages here (already reconciled, closed
      // period). Surfacing the generic fallback instead would hide the one thing the user needs.
      setActionError(error.message || SOMETHING_WENT_WRONG)
    },
    onCompleted(data) {
      const { voidAndReissueCheck } = data || {}
      const { response, reissuedPayable: reissued } = voidAndReissueCheck || {}
      const { status } = response || {}
      if (status === 200 && reissued?.voucherNo) {
        Alert.success(
          `Check voided. Replacement voucher ${reissued.voucherNo} created — print it to assign a new check number.`
        )
        // Close before navigating: the target is this same route with a different param, so the component
        // is not remounted and the confirmation would stay open on top of the new voucher.
        setConfirmationModal(prev => ({ ...prev, show: false }))
        navigate(`/accounts/payable/${reissued.voucherNo}`, { replace: true })
      }
    }
  })

  const handleMarkPaid = () => {
    if (!currentPayable?.id) return

    markPayableAsPaid({
      variables: {
        id: currentPayable.id
      }
    })
  }

  const handleVoidAndReissue = () => {
    if (!currentPayable?.id) return

    voidAndReissueCheck({
      variables: {
        input: { id: currentPayable.id }
      }
    })
  }

  const openConfirmationModal = (action: ConfirmationType) => (e: React.MouseEvent<HTMLLIElement>) => {
    e.preventDefault()
    setActionError(null)
    setConfirmationModal({ show: true, type: action })
    handleMenuClose()
  }

  const closeConfirmationModal = (type: typeof confirmationModal.type) => () => {
    setActionError(null)
    setConfirmationModal({ show: false, type })
  }

  const handleDelete = () => {
    if (!currentPayable?.id) return

    softDeletePayable({
      variables: {
        id: currentPayable.id
      }
    })
  }

  const handleVoidAndDuplicate = () => {
    if (!currentPayable?.id) return

    voidAndDuplicatePayable({
      variables: {
        id: currentPayable.id
      }
    })
  }

  // Replacements created from this payable by Void & Reissue. Normally one; an array because a replacement
  // can itself be voided and reissued, and every link should stay reachable.
  const reissuedAs = (currentPayable?.reissues || []).filter(replacement => !!replacement?.voucherNo)

  const lineItems = currentPayable?.lineItems || []
  const lineItemsTotal = lineItems.reduce((sum, item) => sum + (item.amount || 0), 0)
  // Compared in cents: the amounts arrive as floats, so a direct equality check would flag rounding noise.
  const lineItemsMismatch =
    !!lineItems.length && Math.round(lineItemsTotal * 100) !== Math.round((currentPayable?.amount || 0) * 100)

  const creatorName = [currentPayable?.creator?.firstName, currentPayable?.creator?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim()

  // `createdAt` is a String field over a Date column, so GraphQL serializes it through Date.valueOf()
  // and it arrives as epoch milliseconds in a string ("1788393600000"). Handing that to dayjs as-is
  // parses it as a year (08/03/1790); the digits have to be converted first. `payDate` / `invoiceDate`
  // are DateTime fields and do not need this.
  const createdOn = currentPayable?.createdAt
    ? dayjs(/^\d+$/.test(currentPayable.createdAt) ? Number(currentPayable.createdAt) : currentPayable.createdAt)
    : null

  const glAccount = currentPayable?.lineItems?.find(item => item.glAccount)?.glAccount
  const office = currentPayable?.lineItems?.find(item => item.office)?.office
  const matter = currentPayable?.lineItems?.find(item => item.litigation)?.litigation

  const payableData = [
    ['Voucher No', currentPayable?.voucherNo || '--'],
    ['Payment Method', getProperPaymentMethod(currentPayable?.paymentMethod as PaymentMethod)],
    ['Status', enumToLabel(currentPayable?.status || '')],
    ['Account', currentPayable?.account?.name || '--'],
    ['GL Account', glAccount?.name ? `(${glAccount?.sortCode}) ${glAccount?.name || '--'}` : NOT_APPLICABLE],
    ['Invoice Date', dayjs(currentPayable?.invoiceDate).format('MM/DD/YYYY')],
    ['Pay Date', dayjs(currentPayable?.payDate).format('MM/DD/YYYY')],
    ['Amount', formatCurrency(currentPayable?.amount || 0)],
    [
      currentPayable?.paymentMethod === PaymentMethod.Ach || currentPayable?.paymentMethod === PaymentMethod.Eft
        ? 'Reference No'
        : 'Check No',
      currentPayable?.checkOrAchNumber || NOT_APPLICABLE
    ],
    [
      'Matter',
      matter
        ? `(#${matter?.caseNumber ?? matter?.consultMatterNumber ?? NOT_APPLICABLE}) ${matter?.title || ''}`
        : NOT_APPLICABLE
    ],
    ['Vendor', `(#${currentPayable?.vendor?.vendorId}) ${currentPayable?.vendor?.name || ''}`],
    ['Office', office ? `(#${office?.id}) ${office?.name || ''}` : NOT_APPLICABLE],
    ['Description', currentPayable?.description || NOT_APPLICABLE],
    // Shown for every voucher, whatever its status. Advantage-migrated vouchers have no creator on
    // record, so they read as N/A rather than hiding the row and making its absence look like a bug.
    [
      'Created By',
      creatorName
        ? `${creatorName}${createdOn?.isValid() ? ` on ${createdOn.format('MM/DD/YYYY')}` : ''}`
        : NOT_APPLICABLE
    ],
    // Boolean values render as a tick / cross via the value renderer below.
    ['Reconciled', !!currentPayable?.isReconciled],
    // `reconciledAt` is the statement DATE the payment cleared against (no time of day); `reconciledOn` is the
    // moment the reconciliation was finalized, and is absent for Advantage-migrated payments that arrived
    // already stamped as cleared.
    [
      'Statement Date',
      currentPayable?.reconciledAt ? dayjs(currentPayable.reconciledAt).format('MM/DD/YYYY') : NOT_APPLICABLE
    ],
    [
      'Reconciled On',
      currentPayable?.reconciledOn ? dayjs(currentPayable.reconciledOn).format('hh:mm A MM/DD/YYYY') : NOT_APPLICABLE
    ]
  ]

  useEffect(() => {
    getAccountPayable()
  }, [getAccountPayable])

  // Moving between vouchers (Void & Reissue, Void & Duplicate) keeps this component mounted, since only the
  // route param changes. Anything tied to the previous voucher — an open confirmation, an inline action
  // error — has to be cleared here, or it lingers over the voucher that has just loaded.
  useEffect(() => {
    setConfirmationModal(prev => ({ ...prev, show: false }))
    setActionError(null)
    setOpenCheckModal(false)
  }, [voucherNo])

  // A voided cheque must never be drawn again, and a voucher paid any other way has no leaf to print
  // on — the plan would reject both anyway, so the options are not offered. A Paid cheque still gets
  // them: reprinting an existing check is allowed, and the plan labels it as a reprint.
  const isPrintableCheck =
    currentPayable?.paymentMethod === PaymentMethod.Cheque && currentPayable?.status !== PayableStatus.Voided

  // Pre-fills the modal's bank dropdown from the account this voucher is drawn on.
  const checkBankAccounts: AccountOption[] = currentPayable?.account?.id
    ? [{ value: currentPayable.account.id, label: currentPayable.account.name || '' }]
    : []

  return (
    <Box sx={{ padding: 2 }}>
      <Grid container spacing={2}>
        <Grid item md={8} sm={12}>
          <CardBox marginTop={3}>
            <ContentHead
              title={`Account Payable - ${currentPayable?.voucherNo || '--'}`}
              customButton={actionButton}
              backButton
            />

            {/* A Void & Reissue pair is otherwise invisible from either side: the voided cheque looks simply
                voided, and the replacement looks unrelated. Show the link both ways so the history is
                followable without going through the ledger. */}
            {!!reissuedAs.length && (
              <MuiAlert severity="info" sx={{ mx: 2, mb: 2 }}>
                This check was voided and reissued as{' '}
                {reissuedAs.map((replacement, index) => (
                  <React.Fragment key={replacement.id}>
                    {index > 0 && ', '}
                    <Link to={`/accounts/payable/${replacement.voucherNo}`}>voucher {replacement.voucherNo}</Link>
                  </React.Fragment>
                ))}
                .
              </MuiAlert>
            )}

            {!!currentPayable?.reissuedFrom?.voucherNo && (
              <MuiAlert severity="info" sx={{ mx: 2, mb: 2 }}>
                This check replaces voided{' '}
                <Link to={`/accounts/payable/${currentPayable.reissuedFrom.voucherNo}`}>
                  voucher {currentPayable.reissuedFrom.voucherNo}
                </Link>
                . The original expense and any bill it appears on stay with that voucher.
              </MuiAlert>
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}
            >
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                PaperProps={{
                  sx: { minWidth: '120px' }
                }}
              >
                <MenuItem onClick={handleEditAccount} disabled={currentPayable?.status === PayableStatus.Voided}>
                  Edit
                </MenuItem>

                {isPrintableCheck && (
                  <>
                    <MenuItem onClick={handlePrintCheck}>
                      {currentPayable?.printedAt ? 'Reprint Check' : 'Print Check'}
                    </MenuItem>
                    <MenuItem onClick={handlePreviewCheck} disabled={previewingCheck}>
                      Preview Check
                    </MenuItem>
                  </>
                )}

                {currentPayable?.status === PayableStatus.Unpaid && (
                  <>
                    <MenuItem onClick={openConfirmationModal('markPaid')} disabled={markingAsPaid}>
                      Mark Paid
                    </MenuItem>
                    <MenuItem onClick={openConfirmationModal('delete')}>Delete</MenuItem>
                  </>
                )}

                {currentPayable?.status === PayableStatus.Paid && (
                  <>
                    <MenuItem onClick={openConfirmationModal('void')}>Void</MenuItem>
                    <MenuItem onClick={openConfirmationModal('voidAndDuplicate')} disabled={voidingAndDuplicating}>
                      Void &amp; Duplicate
                    </MenuItem>
                    {SHOW_VOID_AND_REISSUE && currentPayable?.paymentMethod === PaymentMethod.Cheque && (
                      <MenuItem onClick={openConfirmationModal('voidAndReissue')} disabled={voidingAndReissuing}>
                        Void &amp; Reissue Check
                      </MenuItem>
                    )}
                  </>
                )}
              </Menu>
            </Box>
            {payableData.map(([label, value], index) => {
              const content =
                label === 'Account' && currentPayable?.account?.id ? (
                  <Link to={`/accounts/chartered/${currentPayable.account.id}`}>
                    <Typography variant="body2" color={'primary'} noWrap>
                      {value}
                    </Typography>
                  </Link>
                ) : label === 'Matter' && matter?.id ? (
                  <Link to={`/matters/matter-detail/${matter.id}?active-tab=2`}>
                    <Typography variant="body2" color={'primary'} noWrap>
                      {value}
                    </Typography>
                  </Link>
                ) : typeof value === 'string' ? (
                  value
                ) : value ? (
                  <Done color="success" />
                ) : (
                  <Clear color="error" />
                )
              return (
                <React.Fragment key={index}>
                  <CaseDetailRow title={label as string} content={content} />
                </React.Fragment>
              )
            })}
          </CardBox>
        </Grid>

        {currentPayable?.status === PayableStatus.Paid && currentPayable.paymentMethod === PaymentMethod.Cheque && (
          <Grid item md={4} sm={12}>
            <CardBox marginTop={3}>
              <CardBoxTopBar>
                <Typography fontSize="18px">Check Details</Typography>
              </CardBoxTopBar>
              <CaseDetailRow
                title={'Printed On'}
                content={dayjs(currentPayable.actionDetails?.date).format('hh:mm MM/DD/YYYY')}
              />
              <CaseDetailRow
                title={'Printed By'}
                content={`${currentPayable?.actionDetails?.user?.firstName || ''} ${
                  currentPayable?.actionDetails?.user?.lastName || NOT_APPLICABLE
                }`}
              />
            </CardBox>
          </Grid>
        )}

        {!!lineItems.length && (
          <Grid item xs={12}>
            <CardBox>
              <CardBoxTopBar>
                <Typography fontSize="18px">Payable Line Items ({lineItems.length})</Typography>
                {/* Repeated from the footer total row: with a long voucher the footer is the only place
                    the sum appears, and it is the figure that should match the voucher amount. */}
                <Typography fontSize="18px" fontWeight={600} color={lineItemsMismatch ? 'error' : 'inherit'}>
                  Total: {formatCurrency(lineItemsTotal)}
                </Typography>
              </CardBoxTopBar>

              {/* Vouchers routinely carry 50+ lines, so the list is a table inside its own scroll box:
                  the header and the total row stay pinned while the rows scroll, and the page itself
                  does not grow with the line count. */}
              <TableContainer sx={{ maxHeight: 460 }}>
                <Table stickyHeader size="small" aria-label="payable line items">
                  <TableHead>
                    <TableRow>
                      <TableCell width={56}>#</TableCell>
                      <TableCell>Matter</TableCell>
                      <TableCell>Profit Center</TableCell>
                      <TableCell>GL Account</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {lineItems.map((lineItem, index) => {
                      const { litigation, office, glAccount, description, amount, id } = lineItem
                      const matterLabel = litigation
                        ? `(#${litigation.caseNumber ?? litigation.consultMatterNumber ?? NOT_APPLICABLE}) ${
                            litigation.title || ''
                          }`
                        : NOT_APPLICABLE

                      return (
                        <TableRow key={id || index} hover>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            {litigation?.id ? (
                              <Tooltip title={matterLabel} arrow placement="top-start">
                                <Link to={`/matters/matter-detail/${litigation.id}?active-tab=2`}>
                                  <Typography variant="body2" color="primary" noWrap sx={{ maxWidth: 220 }}>
                                    {matterLabel}
                                  </Typography>
                                </Link>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2">{matterLabel}</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{office?.name || NOT_APPLICABLE}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                              {glAccount?.name ? `(${glAccount.sortCode}) ${glAccount.name}` : NOT_APPLICABLE}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={description || ''} arrow placement="top-start">
                              <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>
                                {description || NOT_APPLICABLE}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{formatCurrency(amount || 0)}</Typography>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>

                  {/* Line items are meant to add up to the voucher amount, and some legacy vouchers do not.
                      Calling that out here saves reconciling the two by hand. */}
                  <TableFooter
                    sx={{
                      '& td': {
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 2,
                        backgroundColor: 'white',
                        borderTop: '1px solid',
                        borderColor: 'rgba(0, 0, 0, 0.12)'
                      }
                    }}
                  >
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" fontWeight={600}>
                          Total
                        </Typography>
                        {lineItemsMismatch && (
                          <Typography variant="caption" color="error">
                            Does not match the voucher amount of {formatCurrency(currentPayable?.amount || 0)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(lineItemsTotal)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </CardBox>
          </Grid>
        )}
      </Grid>

      <AddAccountPayableModal
        handleClose={handleEditAccount}
        isOpen={isOpen}
        currentPayable={currentPayable}
        refetch={refetchAccountPayable}
      />

      <CheckModal
        isOpen={openCheckModal}
        handleClose={() => setOpenCheckModal(false)}
        startPrinting={startPrintingCheck}
        vouchers={currentPayable?.voucherNo ? [currentPayable.voucherNo] : []}
        // The run is a single voucher, so the plan table hides its remove control and this never fires;
        // closing is the only sensible reading of "take the last row out".
        onRemoveVoucher={() => setOpenCheckModal(false)}
        bankAccounts={checkBankAccounts}
        defaultCheckNo={currentPayable?.checkOrAchNumber || ''}
      />

      {confirmationModal.show && confirmationModal.type === 'delete' && (
        <ConfirmationModal
          isOpen={confirmationModal.show}
          title="Delete Payable"
          message={`Are you sure you want to delete ${currentPayable?.voucherNo} payable? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          isLoading={softDeleting}
          variant="danger"
          onConfirm={handleDelete}
          onCancel={() => setConfirmationModal({ show: false, type: 'delete' })}
        />
      )}

      {confirmationModal.show && confirmationModal.type === 'void' && currentPayable?.id && (
        <VoidPayableDialog
          payable={currentPayable}
          onClose={closeConfirmationModal('void')}
          onVoided={() => {
            Alert.success('Voided successfully')
            navigate('/accounts/payable', { replace: true })
          }}
        />
      )}

      {confirmationModal.show && confirmationModal.type === 'markPaid' && (
        <ConfirmationModal
          isOpen={confirmationModal.show}
          title="Mark as Paid"
          message="Marking this payable as PAID will update the Account Balance and associated ledger accounts. Are you sure?"
          confirmText="Mark Paid"
          cancelText="Cancel"
          isLoading={markingAsPaid}
          variant="danger"
          onConfirm={handleMarkPaid}
          onCancel={() => setConfirmationModal({ show: false, type: 'markPaid' })}
        />
      )}

      {confirmationModal.show && confirmationModal.type === 'voidAndDuplicate' && (
        <ConfirmationModal
          isOpen={confirmationModal.show}
          title="Void & Duplicate"
          message={`This will void ${currentPayable?.voucherNo} (deleting all related bank payments, expense and journal entries) and create an unpaid duplicate with all line items. Are you sure?`}
          confirmText="Void & Duplicate"
          cancelText="Cancel"
          isLoading={voidingAndDuplicating}
          variant="danger"
          onConfirm={handleVoidAndDuplicate}
          onCancel={closeConfirmationModal('voidAndDuplicate')}
          error={actionError}
        />
      )}

      {confirmationModal.show && confirmationModal.type === 'voidAndReissue' && (
        <ConfirmationModal
          isOpen={confirmationModal.show}
          title="Void & Reissue Check"
          message={
            `Use this when the check itself failed, lost in the mail, or stopped at the bank, but the cost is correct. ` +
            `Check ${
              currentPayable?.checkOrAchNumber || currentPayable?.voucherNo
            } will be reversed with an offsetting journal entry ` +
            `(the original entry is kept, so the month it was issued in does not change) and a new unpaid voucher will be created for reprinting. ` +
            `The matter expense entry, any bill carrying it and the client's ledger are NOT touched, the client is not credited, because they were never refunded. ` +
            `Continue?`
          }
          confirmText="Void & Reissue"
          cancelText="Cancel"
          isLoading={voidingAndReissuing}
          variant="danger"
          onConfirm={handleVoidAndReissue}
          onCancel={closeConfirmationModal('voidAndReissue')}
          error={actionError}
        />
      )}
    </Box>
  )
}

export default PayableDetail
